/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import React, { Fragment } from 'react'
import { sortBy } from 'lodash'
import { css, jsx } from '@emotion/react'

import { EuiFlexGroup, EuiFlexItem, EuiSpacer, EuiTitle, useEuiTheme } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { SaasAuthMfaDeviceResponse } from '@modules/cloud-api/v1/types'
import { deviceTypesDisplayOrder, getAllDeviceTypes } from '@modules/mfa-lib/devices'
import { useFlagsWhenLoaded } from '@modules/launchdarkly'

import DeviceOption from './DeviceOption'
import SelectDevicesOption from './SelectDevicesOption'

const SelectDevice: React.FunctionComponent<{
  devices: SaasAuthMfaDeviceResponse[]
  onSelect: (device: SaasAuthMfaDeviceResponse) => void
}> = ({ devices, onSelect }) => {
  const [_, flags] = useFlagsWhenLoaded()
  const { euiTheme } = useEuiTheme()
  const isMultipleMfaDevicesEnabled = flags.multipleMfaSupport

  const sortedDevices = sortBy(devices, ({ device_type }) => deviceTypesDisplayOrder(device_type))
  const sortedAllDeviceTypes = getAllDeviceTypes()
  return (
    <Fragment>
      <EuiTitle size='m'>
        <h1 style={{ textAlign: 'center' }}>
          <FormattedMessage
            id='mfaLogin.chooseMethod.title'
            defaultMessage='Choose an authentication method'
          />
        </h1>
      </EuiTitle>

      <EuiSpacer size='l' />
      {isMultipleMfaDevicesEnabled ? (
        <EuiFlexGroup
          direction='column'
          alignItems='stretch'
          css={css({
            maxWidth: '48rem',
            [`@media only screen and (min-width: ${euiTheme.breakpoint.m}px)`]: {
              minWidth: '35rem',
            },
          })}
          gutterSize='m'
        >
          {sortedAllDeviceTypes.map((deviceType) => (
            <EuiFlexItem key={`select-devices-option-flex-${deviceType}`}>
              <SelectDevicesOption
                devices={devices.filter((device) => device.device_type === deviceType)}
                type={deviceType}
                onSelect={onSelect}
                key={`select-devices-option-${deviceType}`}
              />
            </EuiFlexItem>
          ))}
        </EuiFlexGroup>
      ) : (
        <EuiFlexGroup direction='column' alignItems='stretch' style={{ maxWidth: '45rem' }}>
          {sortedDevices.map((device) => (
            <EuiFlexItem key={device.device_id}>
              <DeviceOption device={device} onSelect={onSelect} />
            </EuiFlexItem>
          ))}
        </EuiFlexGroup>
      )}
    </Fragment>
  )
}

export default SelectDevice
